import { Button, Form, InputGroup, Spinner } from 'react-bootstrap';
import ProductNameItem from './ProductNameItem';
import { useProductsSelect, type ProductsSelectProps } from './model';

export type { ProductsSelectProps };

const ProductsSelect = (props: ProductsSelectProps) => {
    const {
        preload,
        labelText,
        disabled,
        required,
        search,
        setSearch,
        options,
        selected,
        loading,
        isFixed,
        showResults,
        vendorFilterId,
        vendorFilterLabel,
        canChangeVendorFilter,
        isVendorFilterLocked,
        fetchProducts,
        openVendorFilterDlg,
        handleKeyDown,
        handleSelect,
        handleClear,
        handleCloseResults,
        t,
    } = useProductsSelect(props);

    if (preload) return null;

    const showVendorFilter = canChangeVendorFilter || (isVendorFilterLocked && vendorFilterId != null);

    return (
        <div className="user-selected mb-3">
            {labelText && (
                <div className="d-flex justify-content-between align-items-center">
                    <label className="form-label text-muted mb-1">{labelText}</label>
                    {showVendorFilter &&
                        (canChangeVendorFilter ? (
                            <button
                                type="button"
                                className={`btn btn-link btn-sm p-0 align-baseline mb-1${
                                    vendorFilterId != null ? ' text-danger' : ''
                                }`}
                                disabled={disabled}
                                title={t('selectVendorFilterTitle')}
                                onClick={openVendorFilterDlg}
                            >
                                <i className="bi bi-funnel me-1" aria-hidden="true" />
                                {vendorFilterLabel}
                            </button>
                        ) : (
                            <span
                                className="small text-muted pe-none mb-1"
                                title={t('selectVendorFilterTitle')}
                                aria-disabled="true"
                            >
                                <i className="bi bi-funnel me-1" aria-hidden="true" />
                                {vendorFilterLabel}
                            </span>
                        ))}
                </div>
            )}

            {isFixed ? (
                <div className="user-selected-fixed">
                    <div className="user-selected-fixed-user d-flex align-items-center gap-2 min-w-0">
                        <ProductNameItem product={selected!} />
                    </div>
                    <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="user-selected-clear"
                        disabled={disabled}
                        title={t('common:buttons.clear')}
                        onClick={handleClear}
                    >
                        <i className="bi bi-x-lg" />
                    </Button>
                </div>
            ) : (
                <div className={`user-selected-panel${showResults ? ' is-open' : ''}`}>
                    <InputGroup className="user-selected-search">
                        <Form.Control
                            type="text"
                            value={search}
                            disabled={disabled || loading}
                            required={required}
                            placeholder={t('selectSearchPlaceholder')}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={handleKeyDown}
                        />
                        {showResults && !loading && (
                            <Button
                                type="button"
                                variant="link"
                                className="user-selected-search-btn"
                                disabled={disabled}
                                title={t('common:buttons.close')}
                                onClick={handleCloseResults}
                            >
                                <i className="bi bi-x-lg" />
                            </Button>
                        )}
                        <Button
                            type="button"
                            variant="link"
                            className="user-selected-search-btn"
                            disabled={disabled || loading}
                            title={t('selectSearchBtn')}
                            onClick={() => fetchProducts()}
                        >
                            {loading ? (
                                <Spinner animation="border" size="sm" variant="primary" />
                            ) : (
                                <i className="bi bi-search" />
                            )}
                        </Button>
                    </InputGroup>

                    {showResults && (
                        <div
                            className="user-selected-results position-relative"
                            style={{ maxHeight: 220 }}
                        >
                            {loading && (
                                <div className="user-selected-empty">
                                    <Spinner
                                        animation="border"
                                        size="sm"
                                        variant="primary"
                                        className="opacity-75"
                                    />
                                </div>
                            )}

                            {!loading && options.length === 0 && (
                                <div className="user-selected-empty">
                                    <i className="bi bi-box-seam fs-5 d-block mb-1 opacity-50" />
                                    {t('selectNoResults')}
                                </div>
                            )}

                            {!loading && options.length > 0 && (
                                <ul className="user-selected-list">
                                    {options.map((option) => (
                                        <li key={option.value}>
                                            <button
                                                type="button"
                                                className="user-selected-item"
                                                disabled={disabled}
                                                onClick={() => handleSelect(option)}
                                            >
                                                <ProductNameItem product={option} />
                                                <i className="bi bi-chevron-right user-selected-item-chevron" />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ProductsSelect;
